import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import {
  lineElementClasses,
  markElementClasses,
  ChartsGrid,
  CurveType,
  LineChartPro,
  LinePlot,
  MarkPlot,
  ChartsXAxis,
} from "@mui/x-charts-pro";
import { CustomItemTooltip } from "../../../../../../ResuableChartTooltip/CustomItemTooltip";

import { useDrawingArea } from "@mui/x-charts/hooks";
import { useSelector } from "react-redux";

function CustomBackground({ yAxisMax }: { yAxisMax: number }) {
  const { top, height, width } = useDrawingArea();
  // Calculate zone heights based on ttfb thresholds

  const greenThreshold = 0.7;
  const redThreshold = 1.8;

  // Calculate proportional heights based on y-axis scale
  const greenZoneHeight = Math.min(greenThreshold / yAxisMax, 1) * height;
  const yellowZoneHeight =
    yAxisMax > greenThreshold
      ? Math.min(
          (Math.min(redThreshold, yAxisMax) - greenThreshold) / yAxisMax,
          1
        ) * height
      : 0;
  const redZoneHeight =
    yAxisMax > redThreshold
      ? ((yAxisMax - redThreshold) / yAxisMax) * height
      : 0;

  return (
    <>
      <rect
        x={50}
        y={top + height - greenZoneHeight}
        width={width}
        height={greenZoneHeight}
        fill="#EDF7ED"
        style={{ zIndex: "-999" }}
      />
      {/* Yellow zone (Needs Improvement) - 2.5s to 4s */}
      {yellowZoneHeight > 0 && (
        <rect
          x={50}
          y={top + height - greenZoneHeight - yellowZoneHeight}
          width={width}
          height={yellowZoneHeight}
          fill="#ffffffff"
          style={{ zIndex: "-999" }}
        />
      )}

      {/* Red zone (Poor) - 4s and above */}
      {redZoneHeight > 0 && (
        <rect
          x={50}
          y={top}
          width={width}
          height={redZoneHeight}
          fill="#FDEDED"
          style={{ zIndex: "-999" }}
        />
      )}
    </>
  );
}

const TimeFirstByte = () => {
  const { pageSpeedMetricsDetails } = useSelector(
    (state: any) => state.landingPageExperience
  );
  // Process the data from pageSpeedMetricsDetails
  const processedData =
    pageSpeedMetricsDetails?.map((item: any) => ({
      date: item.date,
      ttfb: item.ttfb,
      device: item.device,
    })) || [];

  // Sort by date to ensure proper chronological order
  const sortedData = processedData.sort(
    (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // Create separate series for Desktop and Mobile if needed
  const desktopData = sortedData.filter(
    (item: any) => item.device === "Desktop"
  );
  const mobileData = sortedData.filter((item: any) => item.device === "Mobile");

  // Get unique dates for x-axis
  // Format dates to dd-mm-yyyy
  const uniqueDates = [
    ...new Set(
      sortedData.map((item: any) => {
        const d = new Date(item.date);
        return d.toLocaleDateString("en-GB").replace(/\//g, "-"); // dd-mm-yyyy
      })
    ),
  ];

  // Prepare chart series - you can choose to show both devices or combine them
  const series = [];

  if (desktopData.length > 0) {
    series.push({
      data: desktopData.map((item: any) => item.ttfb),
      color: "#6200EE",
      curve: "linear" as CurveType,
      label: "Desktop",
    });
  }

  if (mobileData.length > 0) {
    series.push({
      data: mobileData.map((item: any) => item.ttfb),
      color: "#FF6B35",
      curve: "linear" as CurveType,
      label: "Mobile",
    });
  }

  // If no data available, show empty state
  if (!pageSpeedMetricsDetails || pageSpeedMetricsDetails.length === 0) {
    return (
      <>
        <Typography fontSize={18}>TTFB (Time to First Byte)</Typography>
        <Typography sx={{ color: "#00000099", paddingBottom: "14px" }}>
          in Seconds
        </Typography>
        <Box sx={{ padding: "20px", textAlign: "center", color: "#666" }}>
          No data available
        </Box>
      </>
    );
  }

  // Calculate max value for y-axis
  const allttfbValues = sortedData
    .map((item: any) => item.ttfb)
    .filter((val: any) => val != null);
  const maxttfb = Math.max(...allttfbValues);
  // Set y-axis max to appropriate value based on data
  let yAxisMax = 2.5; // Default to show at least the green zone
  if (maxttfb > 4) {
    yAxisMax = Math.ceil(maxttfb * 1.1);
  } else if (maxttfb > 0.7) {
    yAxisMax = 1.8; // Show green, yellow, and partial red zones
  } else {
    yAxisMax = 1.8; // Show only green zone
  }

  const chartProps = {
    height: 300,
    series: series,
  };

  return (
    <>
      <Typography fontSize={18}>TTFB (Time to First Byte)</Typography>
      <Typography sx={{ color: "#00000099", paddingBottom: "14px" }}>
        in Seconds
      </Typography>

      <Stack sx={{ width: "100%", mb: "16px" }}>
        <Box sx={{ width: "100%" }}>
          <LineChartPro
            {...chartProps}
            xAxis={[
              {
                scaleType: "band",
                id: "my-x-axis",
                data: uniqueDates,
                disableLine: true,
                disableTicks: false,
              },
            ]}
            yAxis={[
              {
                id: "y-axis",
                scaleType: "linear",
                position: "right",
                disableLine: true,
                disableTicks: true,
                min: 0,
                max: yAxisMax,
                tickNumber:
                  yAxisMax <= 2.5 ? 6 : yAxisMax <= 4.5 ? 10 : undefined,
              },
            ]}
            margin={{ left: 50, right: 70, top: 25, bottom: 60 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 3,
                zIndex: "99",
                "&:hover": {
                  strokeWidth: "4",
                },
              },
              [`& .MuiChartsAxis-tickLabel`]: {
                fill: "grey !important", // Label color for X and Y axes
              },
              [`& .MuiChartsAxis-tick`]: {
                stroke: "grey !important", // Tick color for X and Y axes
              },
              [`& .MuiChartsLegend-root text`]: {
                fill: "grey !important", // Change legend text color
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: "2",
                  opacity: "0.6",
                },
              },
            }}
            slotProps={{
              legend: {
                direction: "row",
                position: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                labelStyle: {
                  fontSize: 12,
                },
                itemMarkWidth: 12,
                itemMarkHeight: 2,
                markGap: 8,
                itemGap: 10,
                padding: { top: 20, bottom: 5, left: 10, right: 10 },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <CustomBackground yAxisMax={yAxisMax} />
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <LinePlot />
            <MarkPlot />
            <ChartsXAxis axisId="my-x-axis" />
          </LineChartPro>
        </Box>
      </Stack>
    </>
  );
};

export default TimeFirstByte;
